import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";
import HeroCanvas from "../../components/HeroCanvas";
import { GoogleIcon, AppleIcon } from "../../components/OAuthIcons";
import heroImage from "../../assests/hero-race.jpg";
import { requestNotificationPermission } from "../../services/notification";
import { getSubscriptionPlans, getLandingPageStats } from "../../services/Dashboard/Table";
import DownloadAppModal from "../../components/DownloadAppModal";
import toast from "react-hot-toast";

const steps = [
  {
    n: "01",
    title: "AI ANALYSES FORM DATA",
    body: "Our models ingest career stats, jockey & horse form, track conditions, barriers across hundreds of daily runners.",
  },
  {
    n: "02",
    title: "MODEL RANKS EVERY HORSE",
    body: "Our proprietory models produce calibrated win probabilities for every horse in every race without market noise.",
  },
  {
    n: "03",
    title: "YOU GET THE EDGE",
    body: "Rankings, probabilities, and top picks delivered before the first race — so you can make informed decisions, not guesses.",
  },
];

const formatPlanPrice = (plan) => {
  const amount = Number(plan.price);

  if (!Number.isFinite(amount)) return "";

  const hasCents = amount % 1 !== 0;
  return `A$${new Intl.NumberFormat("en-US", {
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: hasCents ? 2 : 0
  }).format(amount)}`;
};

export const getPeriodSuffix = (daysInput) => {
  const num = parseInt(daysInput, 10) || 0;
  if (!num) return '';
  if (num === 365 || num === 366) return '/year';
  if (num === 30 || num === 28 || num === 31) return '/month';
  if (num === 7) return '/week';
  if (num === 60) return '/2 months';
  if (num === 90) return '/3 months';
  if (num === 180) return '/6 months';
  return `/ ${num} Days`;
};

const buildPlanTiers = (plans = []) =>
  plans
    .filter((plan) => plan.is_active)
    .map((plan) => ({
      id: plan.id,
      name: plan.name || plan.code?.replaceAll("_", " ").toUpperCase(),
      price: formatPlanPrice(plan),
      period: getPeriodSuffix(plan.period_days),
      description: plan.description || "AI-powered horse racing predictions",
      features: plan.features || [],
      cta: "Sign In",
      href: "/login",
      featured: plan.most_popular || false,
    }));

export default function Dashboard() {
  const [stats, setStats] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [downloadModal, setDownloadModal] = useState({ open: false, planName: "" });
  const navigate = useNavigate();
  const isLoggedIn = Boolean(localStorage.getItem("access_token"));

  const openDownloadModal = (planName) =>
    setDownloadModal({ open: true, planName });
  const closeDownloadModal = () =>
    setDownloadModal({ open: false, planName: "" });

  // Request push-notification permission on mount (non-blocking)
  useEffect(() => {
    requestNotificationPermission().catch(() => { });
  }, []);

  useEffect(() => {
    let isMounted = true;

    getLandingPageStats()
      .then((data) => {
        if (isMounted && data) {
          setStats([
            { label: "Win rate", value: data.win_rate || "33.4%" },
            { label: "Place rate", value: data.place_rate || "68.2%" },
            { label: "Hot picks win rate", value: data.hot_picks_win_rate || "43.7%" },
            { label: "Hot picks place rate", value: data.hot_picks_place_rate || "76.5%" },
          ]);
        }
      })
      .catch((error) => {
        console.error("Error loading stats from API:", error);
        if (isMounted) {
          setStats([
            { label: "Win rate", value: "33.4%" },
            { label: "Place rate", value: "68.2%" },
            { label: "Hot picks win rate", value: "43.7%" },
            { label: "Hot picks place rate", value: "76.5%" },
          ]);
        }
      });

    getSubscriptionPlans()
      .then((plans) => {
        if (!isMounted) return;

        setTiers(buildPlanTiers(plans));
      })
      .catch(() => {
        if (isMounted) setTiers([]);
      })
      .finally(() => {
        if (isMounted) setPlansLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  // Request push-notification permission on mount (non-blocking)
  useEffect(() => {
    requestNotificationPermission().catch(() => { });
  }, []);

  // Request push-notification permission on mount (non-blocking)
  useEffect(() => {
    requestNotificationPermission().catch(() => { });
  }, []);

  return (
    <div className="min-h-screen bg-[var(--color-bg)] text-[var(--color-text)]">
      <Navbar />

      {/* ── Hero ── */}
      <section className="flex justify-center items-center relative overflow-hidden pt-24 px-5 pb-20">
        <img
          src={heroImage}
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-[0.18] z-0"
        />
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(8,11,15,0.6)_0%,rgba(8,11,15,0.3)_50%,rgba(8,11,15,0.95)_100%)] z-[1]" />

        {/* Neural network nodes — full width, faded behind centre text */}
        <div
          className="absolute inset-0 z-[1] opacity-[0.85] pointer-events-none"
          style={{
            maskImage:
              "radial-gradient(ellipse 45% 55% at 50% 50%, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.35) 35%, rgba(0,0,0,0.85) 60%, rgba(0,0,0,1) 80%)",
            WebkitMaskImage:
              "radial-gradient(ellipse 45% 55% at 50% 50%, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.35) 35%, rgba(0,0,0,0.85) 60%, rgba(0,0,0,1) 80%)",
          }}
        >
          <HeroCanvas />
        </div>

        <div className="flex flex-col justify-center items-center relative z-[2] max-w-[1280px] w-full text-center">
          <div className="inline-flex items-center gap-2 py-1.5 px-3.5 rounded-full bg-[var(--color-bg)] border border-[rgba(200,255,0,0.25)] md:text-sm text-xs text-[var(--color-text)]">
            <span className="md:w-2 md:h-2 w-1.5 h-1.5 rounded-full bg-[var(--color-primary)]" />
            Models updated daily
          </div>

          <h1 className="xl:text-[80px] md:text-6xl text-[40px] leading-[0.98] font-bold tracking-[-0.02em] md:mt-6 mt-4">
            THE <span className="text-[var(--color-primary)]">TIPSTER</span> IN
            <br />
            YOUR <span className="text-[var(--color-primary)]">POCKET</span>
          </h1>

          <p className="max-w-[600px] md:text-lg text-base leading-[1.55] text-[var(--color-text-muted)] md:mt-6 mt-4">
            AI-powered horse racing predictions for Australian racing.
            Data-driven rankings and win probabilities, delivered before the first race —
            brought to you by{" "}
            <span className="brand-wordmark text-[var(--color-text-muted)] font-extrabold">
              your
            </span>{" "}
            <span className="brand-wordmark text-[var(--color-text)] font-extrabold">
              FORM
            </span>
            <span className="brand-wordmark text-[var(--color-primary)] font-extrabold">
              {" "}GURU
            </span>
          </p>

          <div className="flex flex-col items-center md:mt-9 mt-6 gap-6">
            <div className="flex flex-wrap justify-center gap-3">
              <Link
                to="/predictions"
                className="flex items-center gap-2 xl:text-base md:text-sm text-xs bg-[var(--color-primary)] text-black border-none py-3 px-8 rounded-[var(--radius-md)] font-extrabold uppercase tracking-[0.06em] cursor-pointer"
              >
                See Today's Picks <ArrowForwardRoundedIcon sx={{ fontSize: 20 }} />
              </Link>
              {!localStorage.getItem("access_token") && (
                <Link
                  to="/login"
                  className="border border-[var(--color-border)] bg-transparent text-[var(--color-primary)] xl:text-base md:text-sm text-xs py-3 px-8 rounded-[var(--radius-md)] font-extrabold uppercase tracking-[0.06em] cursor-pointer"
                >
                  Sign In
                </Link>
              )}
            </div>

            {/* OAuth */}
            {!localStorage.getItem("access_token") && (
              <div className="flex flex-wrap justify-center md:gap-3 gap-6">
                <button
                  type="button"
                  onClick={() => navigate("/login?trigger=google")}
                  className="flex items-center justify-center gap-3 py-3 px-6 bg-[var(--color-text)] text-black border-none rounded-[var(--radius-md)] xl:text-base md:text-sm text-xs font-semibold font-[inherit] cursor-pointer"
                >
                  <GoogleIcon size={18} />
                  Continue with Google
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/login?trigger=apple")}
                  className="flex items-center justify-center gap-3 py-3 px-6 bg-black text-[var(--color-text)] border border-[var(--color-border)] rounded-[var(--radius-md)] xl:text-base md:text-sm text-xs font-semibold font-[inherit] cursor-pointer"
                >
                  <AppleIcon size={18} />
                  Continue with Apple
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── Stats bar ── */}
      <section className="flex justify-center items-center border-t border-b border-[var(--color-border)] bg-[var(--color-surface)] py-10 px-5">
        <div className="max-w-[1280px] w-full grid md:grid-cols-4 grid-cols-2 gap-6">
          {stats.map((s) => (
            <div key={s.label} className="text-center">
              <p className="tabular xl:text-[28px] md:text-2xl text-xl font-bold m-0 text-[var(--color-text)]">
                {s.value}
              </p>
              <p className="md:text-sm text-xs mt-1 uppercase tracking-[0.08em] text-[var(--color-text-muted)]">
                {s.label}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* ── How it works ── */}
      <section className="flex justify-center items-center xl:py-20 md:py-[60px] py-10 px-5">
        <div className="max-w-[1280px] w-full">
          <h2 className="text-center xl:text-4xl md:text-3xl text-2xl font-bold tracking-[-0.01em] m-0">
            HOW IT <span className="text-[var(--color-primary)]">WORKS</span>
          </h2>
          <p className="text-center xl:text-base md:text-sm text-xs text-[var(--color-text-muted)] mt-3">
            One model. Thousands of data points. One clear ranking.
          </p>
          <div className="grid grid-cols-[repeat(auto-fit,minmax(260px,1fr))] xl:mt-12 md:mt-10 mt-8 gap-6">
            {steps.map((step) => (
              <div
                key={step.title}
                className="relative bg-[var(--color-surface)] border border-[var(--color-border)] rounded-[var(--radius-md)] md:p-8 p-6 overflow-hidden"
              >
                <div className="absolute top-0 left-0 right-0 h-0.5 bg-[linear-gradient(90deg,transparent,var(--color-primary),transparent)] opacity-40" />
                <div className="flex flex-col justify-center gap-3">
                  <span className="xl:text-[32px] md:text-[28px] text-2xl font-bold text-[var(--color-text-light)]">
                    {step.n}
                  </span>
                  <h3 className="xl:text-base md:text-sm text-xs font-bold tracking-[0.06em] text-[var(--color-text)]">
                    {step.title}
                  </h3>
                  <p className="xl:text-base md:text-sm text-xs leading-[1.55] text-[var(--color-text-muted)]">
                    {step.body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Pricing ── */}
      <section className="flex justify-center items-center border-t border-[var(--color-border)] xl:py-20 md:py-[60px] py-10 px-5">
        <div className="max-w-[1280px] w-full">
          <h2 className="text-center xl:text-4xl md:text-3xl text-2xl font-bold tracking-[-0.01em] m-0">
            SIMPLE <span className="text-[var(--color-primary)]">PRICING</span>
          </h2>
          <p className="text-center xl:text-base md:text-sm text-xs text-[var(--color-text-muted)] mt-3">
            Access is available only to verified accounts.
          </p>
          <div className="grid lg:grid-cols-3 md:grid-cols-2 grid-cols-1 xl:mt-12 md:mt-10 mt-8 gap-6">
            {plansLoading ? (
              <div className="col-span-full min-h-[280px] grid place-items-center border border-[var(--color-border)] rounded-[var(--radius-md)] bg-[var(--color-surface)] text-[var(--color-text-muted)] xl:text-base md:text-sm text-xs uppercase tracking-[0.08em]">
                Loading plans...
              </div>
            ) : tiers.length === 0 ? (
              <div className="col-span-full min-h-[280px] grid place-items-center border border-[var(--color-border)] rounded-[var(--radius-md)] bg-[var(--color-surface)] text-[var(--color-text-muted)] xl:text-base md:text-sm text-xs uppercase tracking-[0.08em]">
                No subscription plans available.
              </div>
            ) : tiers.map((tier) => (
              <div
                key={tier.name}
                className="flex flex-col group bg-[var(--color-surface)] rounded-[var(--radius-md)] relative overflow-hidden cursor-pointer md:min-h-[560px] min-h-[450px] md:p-8 p-6 transition-all duration-[400ms] ease-[cubic-bezier(0.16,1,0.3,1)] border border-[var(--color-border)] hover:border-[var(--color-primary)] hover:scale-[1.02] hover:z-10 hover:shadow-[0_20px_40px_-15px_rgba(200,255,0,0.15)] z-[1]"
              >
                <div className="absolute top-0 left-0 right-0 h-0.5 bg-[linear-gradient(90deg,transparent,var(--color-primary),transparent)] opacity-40" />
                <div className="absolute top-0 left-0 right-0 h-[3px] bg-[var(--color-primary)] opacity-0 -translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300 ease-out" />
                {tier.featured && (
                  <span className="inline-block py-1.5 px-3 rounded-full md:text-xs text-[10px] w-fit font-bold uppercase tracking-[0.06em] bg-[rgba(200,255,0,0.12)] text-[var(--color-primary)] border border-[rgba(200,255,0,0.25)] mb-4">
                    Most popular
                  </span>
                )}
                <h3 className="md:text-xl text-lg font-bold m-0 tracking-[-0.01em]">
                  {tier.name}
                </h3>
                <p className="xl:text-base md:text-sm text-xs text-[var(--color-text-muted)] mt-1">
                  {tier.description}
                </p>
                <div className="mt-6 flex items-baseline gap-1">
                  <span className="tabular xl:text-[32px] md:text-[28px] text-2xl font-bold">
                    {tier.price}
                  </span>
                  <span className="xl:text-base md:text-sm text-xs text-[var(--color-text-muted)]">
                    {tier.period}
                  </span>
                </div>
                <ul className="grid list-none p-0 mt-8 mb-12 md:gap-3 gap-2">
                  {tier.features.map((f) => (
                    <li
                      key={f}
                      className="flex items-center xl:text-base md:text-sm text-xs gap-2 text-[var(--color-text)]"
                    >
                      <span className="text-[var(--color-primary)] font-bold">✓</span>
                      {f}
                    </li>
                  ))}
                </ul>
                {isLoggedIn ? (
                  <button
                    type="button"
                    onClick={() => openDownloadModal(tier.name)}
                    className="w-full text-center rounded-[var(--radius-md)] xl:text-base md:text-sm text-xs font-extrabold mt-auto p-3 uppercase tracking-[0.06em] transition-all duration-300 ease-in-out bg-transparent text-[var(--color-text)] border border-[var(--color-border)] group-hover:bg-[var(--color-primary)] group-hover:text-black group-hover:border-transparent cursor-pointer"
                  >
                    Subscribe
                  </button>
                ) : (
                  <Link
                    to={tier.href}
                    className="w-full text-center rounded-[var(--radius-md)] xl:text-base md:text-sm text-xs font-extrabold mt-auto p-3 uppercase tracking-[0.06em] transition-all duration-300 ease-in-out bg-transparent text-[var(--color-text)] border border-[var(--color-border)] group-hover:bg-[var(--color-primary)] group-hover:text-black group-hover:border-transparent cursor-pointer"
                  >
                    {tier.cta}
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />

      <DownloadAppModal
        isOpen={downloadModal.open}
        onClose={closeDownloadModal}
        planName={downloadModal.planName}
      />
    </div>
  );
}
